import _ from 'lodash';
import React from 'react';

import { VirtualTable } from '@devexpress/dx-react-grid-material-ui';
import { makeStyles } from '@mui/styles';
import { TableStateContext } from '../TableStateContext';

export const rowStyleNames = {
  DEFAULT: 'default',
  DISABLED: 'disabled',
  EXPANDABLE_ROW: 'expandableRow',
  CLICKABLE: 'clickable',
  P100: 'p100',
  P250: 'p250',
  NOT_SUPPORTED: 'notSupported',
  ENDORSEMENT: 'endorsement',
};

export const useRowStyles = makeStyles(({ palette }) => ({
  default: {
    backgroundColor: palette.background.tableRow,
  },
  expandableRow: {
    backgroundColor: palette.background.tableRow,
    '&:hover': {
      backgroundColor: palette.background.tableRowHover,
      cursor: 'default',
    },
  },
  clickable: {
    backgroundColor: palette.background.tableRow,
    '&:hover': {
      backgroundColor: palette.background.tableRowHover,
      cursor: 'pointer',
    },
  },
  p100: {
    backgroundColor: palette.background.paper,
    '&:hover': { backgroundColor: palette.background.tableRowHover },
  },
  p250: {
    backgroundColor: palette.tables.productRow,
    '&:hover': { backgroundColor: palette.background.tableRowHover },
  },
  notSupported: {
    backgroundColor: palette.background.paper,
    '&:hover': { backgroundColor: palette.background.tableRowHover },
  },
  endorsement: {
    backgroundColor: palette.tables.endorsement,
    '&:hover': { backgroundColor: palette.background.tableRowHover },
  },
  disabled: {
    backgroundColor: palette.background.tableRowDisabled,
    '&:hover': { backgroundColor: palette.background.tableRowDisabled },
  },
}));

const deriveProductStylingKey = (row: any, enableEndorsementLogic = true) => {
  if (enableEndorsementLogic && row.isEndorsement) {
    return rowStyleNames.ENDORSEMENT;
  }

  const productType = row.product ?? row.productType;
  switch (productType) {
    case 'P100':
      return rowStyleNames.P100;
    case 'P250':
      return rowStyleNames.P250;
    case 'NotSupported':
      return rowStyleNames.NOT_SUPPORTED;
    default:
      return rowStyleNames.DEFAULT;
  }
};

export const DefaultTableRow = ({ row, ...props }: any) => {
  const classes = useRowStyles();
  return (
    <VirtualTable.Row
      className={classes[rowStyleNames.DEFAULT]}
      data-qa={`TABLE_ROW:${props.tableRow?.rowId}`}
      {...props}
    />
  );
};

// Guards a row's onClick against text-selection drags (via the mousedown/mouseup
// timing gap) and against inner elements opted out via a `disableRowDetail` class.
export const useRowClickGuard = (onClick?: (e: any) => void) => {
  const [mouseDown, setMouseDown] = React.useState(null);
  const [mouseUp, setMouseUp] = React.useState(null);

  const handleClick = (e: any) => {
    const classList = _.get(e, 'target.classList', '');
    if (
      typeof onClick == 'function' &&
      mouseUp - mouseDown < 500 &&
      ![...classList].includes('disableRowDetail')
    ) {
      onClick(e);
    }
  };

  return {
    onMouseDown: () => setMouseDown(new Date()),
    onMouseUp: () => setMouseUp(new Date()),
    onClick: handleClick,
  };
};

export const ExpandableRow = ({ ...props }) => {
  // @ts-expect-error TS(2339): Property 'handlers' does not exist on type 'unknow... Remove this comment to see the full error message
  const { handlers } = React.useContext(TableStateContext);

  return (
    <ExpandableRowBase onClick={handlers.onExpandedRowIdsChange} {...props} />
  );
};

export const ExpandableRowBase = ({ row, onClick, ...props }: any) => {
  const classes = useRowStyles();
  const clickGuard = useRowClickGuard(
    onClick ? () => onClick(row.id) : undefined
  );

  return (
    <VirtualTable.Row
      {...clickGuard}
      className={classes[rowStyleNames.EXPANDABLE_ROW]}
      data-qa={`TABLE_ROW:${props.tableRow?.rowId}`}
      {...props}
    />
  );
};

export const AdminQuotesTableRow = ({ row, ...props }: any) => {
  const classes = useRowStyles();
  const styleKey = deriveProductStylingKey(row);

  return <ExpandableRow {...props} row={row} className={classes[styleKey]} />;
};

export const CowbellPoliciesTableRow = ({ row, ...props }: any) => {
  const classes = useRowStyles();
  const styleKey = deriveProductStylingKey(row, false);

  return <ExpandableRow {...props} row={row} className={classes[styleKey]} />;
};

export const AgencyPoliciesTableRow = ({ row, ...props }: any) => {
  const classes = useRowStyles();
  const styleKey = deriveProductStylingKey(row, true);

  return <ExpandableRow {...props} row={row} className={classes[styleKey]} />;
};

export const CowbellAccountsTableRow = ({ row, ...props }: any) => {
  const classes = useRowStyles();
  const styleKey = deriveProductStylingKey({}, false);

  return <ExpandableRow {...props} row={row} className={classes[styleKey]} />;
};

export const SubmissionTableRow = ({ row, ...props }: any) => {
  const classes = useRowStyles();
  const styleKey = deriveViableStylingKey(row);

  return <ExpandableRow {...props} row={row} className={classes[styleKey]} />;
};

export const OfacTableRow = ({ row, ...props }: any) => {
  const classes = useRowStyles();
  let styleKey = deriveViableStylingKey(row);

  if (row.archived) {
    styleKey = rowStyleNames.DISABLED;
  }

  return (
    <ExpandableRow
      {...props}
      row={row}
      className={classes[styleKey as keyof typeof classes]}
    />
  );
};

const deriveViableStylingKey = (row: any) => {
  if (!row.viable) {
    return rowStyleNames.P250;
  }
  return rowStyleNames.DEFAULT;
};
